import { useState, useEffect, useCallback } from 'react';
import MdLicenseBanner from "../components/MdLicenseBanner";
import StatusTooltip from "../components/StatusTooltip";
import CertStatusPanel from "../components/CertStatusPanel";
import {
  Heart, Loader2, RefreshCw, Zap, Signal, Wifi, Clock,
  Activity, Monitor, Video, HardDrive, Server, Network,
  Router, Lock, Smartphone, ArrowDown, ArrowUp,
  AlertTriangle, XCircle, CheckCircle, ChevronDown, ChevronRight,
  Bug, Shield, Info, Wrench, ExternalLink, X, BellOff,
  PlayCircle, PauseCircle, MessageSquare, HelpCircle
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import healthTutorial from '../components/tutorials/configs/healthTutorial';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import useAuthStore from '../stores/authStore';

const COLORS = {
  primary: '#00d4aa',
  secondary: '#0ea5e9',
  warning: '#f59e0b',
  danger: '#ef4444',
  success: '#22c55e',
  muted: '#64748b',
};

const formatDeviceType = (type) => {
  if (!type) return 'Unknown';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    .replace('Vms Server', 'VMS').replace('Nvr', 'NVR');
};

const DeviceTypeIcon = ({ type, size = 16, color }) => {
  const icons = {
    CAMERA: Video, NVR: HardDrive, VMS_SERVER: Server,
    SERVER: Server, SWITCH: Network, ROUTER: Router,
    ACCESS_CONTROLLER: Lock, INTERCOM: Smartphone,
  };
  const Icon = icons[type] || Monitor;
  return <Icon size={size} color={color} />;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="md-chart-tooltip">
      <div className="md-chart-tooltip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="md-chart-tooltip-item" style={{ color: p.color }}>
          <span className="md-chart-tooltip-dot" style={{ background: p.color }} />
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toFixed(1) : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

const SEVERITY_CONFIG = {
  CRITICAL: { icon: XCircle, color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'Critical' },
  WARNING: { icon: AlertTriangle, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: 'Warning' },
  INFO: { icon: Info, color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)', label: 'Info' },
};

const ERROR_STATUS_CONFIG = {
  ACTIVE: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'Active' },
  IN_PROGRESS: { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', label: 'In Progress' },
  SNOOZED: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', label: 'Snoozed' },
  RESOLVED: { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', label: 'Resolved' },
  AUTO_RESOLVED: { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', label: 'Auto-Resolved' },
};

const SNOOZE_OPTIONS = [
  { label: '1 hour', ms: 60 * 60 * 1000 },
  { label: '4 hours', ms: 4 * 60 * 60 * 1000 },
  { label: '8 hours', ms: 8 * 60 * 60 * 1000 },
  { label: '24 hours', ms: 24 * 60 * 60 * 1000 },
  { label: '3 days', ms: 3 * 24 * 60 * 60 * 1000 },
  { label: '1 week', ms: 7 * 24 * 60 * 60 * 1000 },
];

const formatRelativeTime = (date) => {
  if (!date) return 'Never';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// ── Error Action Modal ──

const ErrorActionModal = ({ error, onClose, onSubmit, onReportBug }) => {
  const [action, setAction] = useState('IN_PROGRESS');
  const [notes, setNotes] = useState(error.notes || '');
  const [snoozeDuration, setSnoozeDuration] = useState(SNOOZE_OPTIONS[3].ms); // default 24h
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    if (action === 'REPORT_BUG') {
      await onReportBug(error.id, { notes: notes.trim() || undefined });
    } else {
      const payload = { status: action, notes: notes.trim() || undefined };
      if (action === 'SNOOZED') {
        payload.snoozedUntil = new Date(Date.now() + snoozeDuration).toISOString();
      }
      await onSubmit(error.id, payload);
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content md-error-action-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><MessageSquare size={18} /> Update Error Status</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="md-error-action-context">
            <span className="md-error-severity-badge" style={{ background: SEVERITY_CONFIG[error.severity]?.bg, color: SEVERITY_CONFIG[error.severity]?.color }}>
              {error.severity}
            </span>
            <span>{error.simple}</span>
          </div>
          {error.deviceName && (
            <p className="md-error-action-device">{error.deviceName} ({error.deviceIp})</p>
          )}

          <div className="form-group" style={{ marginTop: 16 }}>
            <label>Action</label>
            <div className="md-error-action-options">
              <label className={`md-error-action-option ${action === 'IN_PROGRESS' ? 'selected' : ''}`}>
                <input type="radio" name="action" value="IN_PROGRESS" checked={action === 'IN_PROGRESS'} onChange={() => setAction('IN_PROGRESS')} />
                <PlayCircle size={16} style={{ color: '#3b82f6' }} />
                <div>
                  <strong>In Progress</strong>
                  <span>Mark as being worked on</span>
                </div>
              </label>
              <label className={`md-error-action-option ${action === 'SNOOZED' ? 'selected' : ''}`}>
                <input type="radio" name="action" value="SNOOZED" checked={action === 'SNOOZED'} onChange={() => setAction('SNOOZED')} />
                <BellOff size={16} style={{ color: '#8b5cf6' }} />
                <div>
                  <strong>Snooze</strong>
                  <span>Hide temporarily, auto-reactivates</span>
                </div>
              </label>
              <label className={`md-error-action-option ${action === 'RESOLVED' ? 'selected' : ''}`}>
                <input type="radio" name="action" value="RESOLVED" checked={action === 'RESOLVED'} onChange={() => setAction('RESOLVED')} />
                <CheckCircle size={16} style={{ color: '#22c55e' }} />
                <div>
                  <strong>Resolve</strong>
                  <span>Mark as fixed</span>
                </div>
              </label>
              {!error.isCcmBug && (
                <label className={`md-error-action-option ${action === 'REPORT_BUG' ? 'selected' : ''}`}>
                  <input type="radio" name="action" value="REPORT_BUG" checked={action === 'REPORT_BUG'} onChange={() => setAction('REPORT_BUG')} />
                  <Bug size={16} style={{ color: '#f97316' }} />
                  <div>
                    <strong>Report as Bug</strong>
                    <span>Escalate to Serverli team for investigation</span>
                  </div>
                </label>
              )}
            </div>
          </div>

          {action === 'SNOOZED' && (
            <div className="form-group">
              <label>Snooze Duration</label>
              <select className="form-select" value={snoozeDuration} onChange={(e) => setSnoozeDuration(parseInt(e.target.value))}>
                {SNOOZE_OPTIONS.map(opt => (
                  <option key={opt.ms} value={opt.ms}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Notes (optional)</label>
            <textarea
              className="form-input md-error-notes-input"
              rows={3}
              placeholder="What was done, what's the plan, why snoozing, bug details..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <><Loader2 size={14} className="md-spin" /> Saving...</> : 'Update Status'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Error Row Component (expandable) ──

const ErrorRow = ({ error, onResolve, onOpenActionModal }) => {
  const [expanded, setExpanded] = useState(false);
  const sev = SEVERITY_CONFIG[error.severity] || SEVERITY_CONFIG.WARNING;
  const SevIcon = sev.icon;
  const statusCfg = ERROR_STATUS_CONFIG[error.errorStatus] || ERROR_STATUS_CONFIG.ACTIVE;

  return (
    <div className="md-error-row">
      <div className="md-error-row-summary" onClick={() => setExpanded(!expanded)}>
        <div className="md-error-row-left">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="md-error-severity-badge" style={{ background: sev.bg, color: sev.color }}>
            <SevIcon size={12} /> {sev.label}
          </span>
          <span className="md-error-simple">{error.simple}</span>
          {error.isCcmBug && (
            <span className="md-error-ccm-badge">
              <Bug size={11} /> Reported to Serverli
            </span>
          )}
          {error.errorStatus && error.errorStatus !== 'ACTIVE' && (
            <span className="md-error-status-badge" style={{ background: statusCfg.bg, color: statusCfg.color }}>
              {statusCfg.label}
            </span>
          )}
        </div>
        <div className="md-error-row-right">
          <span className="md-error-category">{error.category}</span>
          {error.occurrences > 1 && (
            <span className="md-error-count">{error.occurrences}x</span>
          )}
          <span className="md-error-time">{formatRelativeTime(error.lastSeenAt)}</span>
        </div>
      </div>

      {expanded && (
        <div className="md-error-row-detail">
          {/* Simplified explanation */}
          {error.detail && (
            <div className="md-error-detail-section">
              <h5>What This Means</h5>
              <p>{error.detail}</p>
            </div>
          )}

          {/* Notes if present */}
          {error.notes && (
            <div className="md-error-detail-section md-error-notes-display">
              <h5><MessageSquare size={13} /> Notes</h5>
              <p>{error.notes}</p>
            </div>
          )}

          {/* Snoozed until */}
          {error.errorStatus === 'SNOOZED' && error.snoozedUntil && (
            <div className="md-error-detail-section">
              <h5><BellOff size={13} /> Snoozed Until</h5>
              <p>{new Date(error.snoozedUntil).toLocaleString()}</p>
            </div>
          )}

          {/* Fix steps (only if not CCM bug) */}
          {error.isCcmBug === false && error.fixSteps && error.fixSteps.length > 0 && (
            <div className="md-error-detail-section">
              <h5><Wrench size={13} /> How to Fix</h5>
              <ol className="md-error-fix-steps">
                {error.fixSteps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          )}

          {/* CCM bug message */}
          {error.isCcmBug && (
            <div className="md-error-ccm-notice">
              <Shield size={14} />
              <div>
                <strong>This is a Serverli platform issue.</strong>
                <p>Our team has been notified and is working on a fix. No action is needed on your end.</p>
              </div>
            </div>
          )}

          {/* Raw error (collapsible) */}
          <details className="md-error-raw-details">
            <summary>Technical Details</summary>
            <code className="md-error-raw-code">{error.raw}</code>
            {error.bugCode && <span className="md-error-bugcode">Bug ID: {error.bugCode}</span>}
          </details>

          {/* Actions */}
          <div className="md-error-actions">
            <button className="btn btn-sm md-error-action-btn work" onClick={(e) => { e.stopPropagation(); onOpenActionModal(error); }}>
              <PlayCircle size={12} /> Update Status
            </button>
            {error.errorStatus !== 'RESOLVED' && (
              <button className="btn btn-sm md-error-action-btn resolve" onClick={(e) => { e.stopPropagation(); onResolve(error.id); }}>
                <CheckCircle size={12} /> Quick Resolve
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Device Error Group ──

const DeviceErrorGroup = ({ group, onResolve, onOpenActionModal }) => {
  const [expanded, setExpanded] = useState(true);
  const worstSev = SEVERITY_CONFIG[group.worstSeverity] || SEVERITY_CONFIG.WARNING;
  const WorstIcon = worstSev.icon;

  return (
    <div className="md-error-device-group">
      <div className="md-error-device-header" onClick={() => setExpanded(!expanded)}>
        <div className="md-error-device-left">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <DeviceTypeIcon type={group.deviceType} size={16} color={worstSev.color} />
          <div className="md-error-device-info">
            <span className="md-error-device-name">{group.deviceName}</span>
            {group.deviceIp && <span className="md-error-device-ip">{group.deviceIp}</span>}
          </div>
        </div>
        <div className="md-error-device-right">
          <span className="md-error-severity-badge" style={{ background: worstSev.bg, color: worstSev.color }}>
            <WorstIcon size={12} /> {group.errors.length} error{group.errors.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="md-error-device-errors">
          {group.errors.map(err => (
            <ErrorRow key={err.id} error={err} onResolve={onResolve} onOpenActionModal={onOpenActionModal} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main Component ──

const MdHealth = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(healthTutorial);

  const { tenantVersion } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [timeRange, setTimeRange] = useState('24h');
  const [latencyData, setLatencyData] = useState([]);
  const [packetLossData, setPacketLossData] = useState([]);

  // Error state
  const [errorsLoading, setErrorsLoading] = useState(true);
  const [errorData, setErrorData] = useState({ devices: [], totalErrors: 0, totalDevices: 0 });
  const navigateHealth = useNavigate();
  const [errorStats, setErrorStats] = useState({ total: 0, critical: 0, warnings: 0, ccmBugs: 0 });
  const [errorFilter, setErrorFilter] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [resolvedData, setResolvedData] = useState({ devices: [], totalErrors: 0, totalDevices: 0 });
  const [resolvedLoading, setResolvedLoading] = useState(false);

  // Action modal
  const [actionModalError, setActionModalError] = useState(null);

  // Reset on tenant switch
  useEffect(() => {
    setDevices([]); setLoading(true);
    setErrorData({ devices: [], totalErrors: 0, totalDevices: 0 });
    setErrorsLoading(true);
  }, [tenantVersion]);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/devices');
      if (res.ok) {
        const data = await res.json();
        setDevices(data.devices || data || []);
      }

      // TODO: Replace with real /api/md/metrics endpoint
      const now = Date.now();
      setLatencyData(Array.from({ length: 48 }, (_, i) => {
        const t = new Date(now - (48 - i) * 15 * 60000);
        return {
          time: t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          latency: 3 + Math.random() * 12 + Math.sin(i / 8) * 4,
        };
      }));
      setPacketLossData(Array.from({ length: 24 }, (_, i) => {
        const t = new Date(now - (24 - i) * 60 * 60000);
        return {
          time: t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          packetLoss: Math.random() * 2.5,
        };
      }));
    } catch (error) {
      console.error('[MD Health] Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantVersion]);

  const fetchErrors = useCallback(async () => {
    setErrorsLoading(true);
    try {
      const params = new URLSearchParams();
      if (errorFilter) params.set('severity', errorFilter);

      const [errorsRes, statsRes] = await Promise.all([
        fetchWithTenant(`/api/md/health/errors${params.toString() ? '?' + params : ''}`),
        fetchWithTenant('/api/md/health/error-stats'),
      ]);

      if (errorsRes.ok) {
        const data = await errorsRes.json();
        setErrorData(data);
      }
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setErrorStats(stats);
      }
    } catch (error) {
      console.error('[MD Health] Failed to fetch errors:', error);
    } finally {
      setErrorsLoading(false);
    }
  }, [errorFilter]);

  const fetchResolved = useCallback(async () => {
    if (!showResolved) return;
    setResolvedLoading(true);
    try {
      const res = await fetchWithTenant('/api/md/health/errors/resolved?days=30');
      if (res.ok) {
        const data = await res.json();
        setResolvedData(data);
      }
    } catch (error) {
      console.error('[MD Health] Failed to fetch resolved errors:', error);
    } finally {
      setResolvedLoading(false);
    }
  }, [showResolved]);

  useEffect(() => { fetchResolved(); }, [fetchResolved]);
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchErrors(); }, [fetchErrors]);

  const handleResolveError = async (errorId) => {
    try {
      const res = await fetchWithTenant(`/api/md/health/errors/${errorId}/resolve`, { method: 'POST' });
      if (res.ok) {
        toast.success('Error marked as resolved');
        fetchErrors();
      } else {
        toast.error('Failed to resolve error');
      }
    } catch {
      toast.error('Failed to resolve error');
    }
  };

  const handleReportBug = async (errorId, { notes }) => {
    try {
      const res = await fetchWithTenant(`/api/md/health/errors/${errorId}/report-bug`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      if (res.ok) {
        toast.success('Bug reported to Serverli team');
        setActionModalError(null);
        fetchErrors();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to report bug');
      }
    } catch {
      toast.error('Failed to report bug');
    }
  };

  const handleUpdateErrorStatus = async (errorId, payload) => {
    try {
      const res = await fetchWithTenant(`/api/md/health/errors/${errorId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const statusLabels = { IN_PROGRESS: 'marked as in progress', SNOOZED: 'snoozed', RESOLVED: 'resolved' };
        toast.success('Error ' + (statusLabels[payload.status] || 'updated'));
        setActionModalError(null);
        fetchErrors();
      } else {
        toast.error('Failed to update error');
      }
    } catch {
      toast.error('Failed to update error');
    }
  };

  // Aggregate device stats
  const onlineDevices = devices.filter(d => d.status === 'ONLINE');
  const avgHealth = onlineDevices.length > 0
    ? Math.round(onlineDevices.reduce((s, d) => s + (d.healthScore || 0), 0) / onlineDevices.length)
    : 0;
  const avgPing = onlineDevices.length > 0
    ? (onlineDevices.reduce((s, d) => s + (d.lastPingMs || 0), 0) / onlineDevices.length).toFixed(1)
    : '0';

  // Devices needing attention
  const attentionDevices = devices
    .filter(d => d.status !== 'ONLINE' || (d.healthScore != null && d.healthScore < 90))
    .sort((a, b) => (a.healthScore || 0) - (b.healthScore || 0));

  if (loading) {
    return (
      <div className="md-loading">
        <Loader2 size={32} className="md-spin" />
        <span>Loading health metrics...</span>
      </div>
    );
  }

  return (
    <div className="md-page">
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title">
            <Heart size={22} />
            Health Metrics
          </h1>
          <p className="page-subtitle">Detailed performance data across all devices</p>
        </div>
        <div className="page-header-actions">
          <div className="md-time-pills">
            {['1h', '6h', '24h', '7d', '30d'].map(t => (
              <button key={t} className={`md-time-pill ${timeRange === t ? 'active' : ''}`} onClick={() => setTimeRange(t)}>{t}</button>
            ))}
          </div>
          <button className="btn btn-secondary" onClick={() => { fetchData(); fetchErrors(); }}>
            <RefreshCw size={15} /> Refresh
          </button>
          <button className="tutorial-help-btn" onClick={() => startTutorial(healthTutorial.module, healthTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      <MdLicenseBanner />

      {/* Stats */}
      <div className="md-stat-grid">
        <div className="md-stat-card">
          <div className="md-stat-icon" style={{ background: `${COLORS.primary}15` }}>
            <Zap size={22} color={COLORS.primary} />
          </div>
          <div className="md-stat-content">
            <span className="md-stat-label">Avg Latency</span>
            <span className="md-stat-value">{avgPing}<span className="md-stat-total">ms</span></span>
            <span className="md-stat-sub">
              <ArrowDown size={11} color={COLORS.success} style={{ display: 'inline' }} /> across online devices
            </span>
          </div>
        </div>

        <div className="md-stat-card">
          <div className="md-stat-icon" style={{ background: `${COLORS.success}15` }}>
            <Signal size={22} color={COLORS.success} />
          </div>
          <div className="md-stat-content">
            <span className="md-stat-label">Avg Health</span>
            <span className="md-stat-value">{avgHealth}<span className="md-stat-total">%</span></span>
            <span className="md-stat-sub">{onlineDevices.length} online devices</span>
          </div>
        </div>

        <div className="md-stat-card">
          <div className="md-stat-icon" style={{ background: `${COLORS.secondary}15` }}>
            <Wifi size={22} color={COLORS.secondary} />
          </div>
          <div className="md-stat-content">
            <span className="md-stat-label">Devices Online</span>
            <span className="md-stat-value">{onlineDevices.length}<span className="md-stat-total">/{devices.length}</span></span>
            <span className="md-stat-sub">{devices.length - onlineDevices.length} offline</span>
          </div>
        </div>

        <div className="md-stat-card">
          <div className="md-stat-icon" style={{ background: `${COLORS.danger}15` }}>
            <AlertTriangle size={22} color={errorStats.total > 0 ? COLORS.danger : COLORS.success} />
          </div>
          <div className="md-stat-content">
            <span className="md-stat-label">Active Errors</span>
            <span className="md-stat-value" style={{ color: errorStats.total > 0 ? COLORS.danger : COLORS.success }}>
              {errorStats.total}
            </span>
            <span className="md-stat-sub">
              {errorStats.critical > 0 ? `${errorStats.critical} critical` : 'No critical errors'}
            </span>
          </div>
        </div>
      </div>

      {/* ═══════════ ERRORS SECTION ═══════════ */}
      <div className="md-panel" style={{ marginBottom: 20 }}>
        <div className="md-panel-header">
          <div className="md-panel-header-left">
            <span className="md-panel-title">
              <AlertTriangle size={16} /> Device Errors
            </span>
            <span className="md-chart-subtitle">
              {errorStats.total} active error{errorStats.total !== 1 ? 's' : ''} ({errorStats.critical} critical)
            </span>
          </div>
          <button className="btn btn-sm" onClick={() => navigateHealth('/notifications')}>
            Open Notification Center
          </button>
        </div>
        <div className="md-panel-body" style={{ textAlign: 'center', padding: '30px 20px' }}>
          {errorStats.total > 0 ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 16 }}>
                {errorStats.critical > 0 && (
                  <span style={{ color: '#ef4444', fontSize: 14 }}>
                    <XCircle size={14} style={{ verticalAlign: -2 }} /> {errorStats.critical} critical
                  </span>
                )}
                {errorStats.warnings > 0 && (
                  <span style={{ color: '#f59e0b', fontSize: 14 }}>
                    <AlertTriangle size={14} style={{ verticalAlign: -2 }} /> {errorStats.warnings} warnings
                  </span>
                )}
                {errorStats.ccmBugs > 0 && (
                  <span style={{ color: '#fb923c', fontSize: 14 }}>
                    <Bug size={14} style={{ verticalAlign: -2 }} /> {errorStats.ccmBugs} platform issues
                  </span>
                )}
              </div>
              <button className="btn btn-primary" onClick={() => navigateHealth('/notifications')}>
                View & Manage Errors
              </button>
            </div>
          ) : (
            <div>
              <CheckCircle size={24} color="#22c55e" style={{ marginBottom: 8 }} />
              <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0 }}>No active errors. All devices operating normally.</p>
            </div>
          )}
        </div>
      </div>

      {/* Certificate Status */}
      <CertStatusPanel />

      {/* Charts */}
      <div className="md-two-col" style={{ marginBottom: 20 }}>
        <div className="md-chart-card">
          <div className="md-chart-header">
            <span className="md-chart-title">Latency Trend</span>
          </div>
          <div className="md-chart-body" style={{ height: 240, padding: '14px 8px 8px' }}>
            <ResponsiveContainer>
              <AreaChart data={latencyData}>
                <defs>
                  <linearGradient id="lgLat" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2d3d" />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} interval={11} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} unit="ms" width={36} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="latency" stroke={COLORS.primary} fill="url(#lgLat)" strokeWidth={2} name="Latency (ms)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="md-chart-card">
          <div className="md-chart-header">
            <span className="md-chart-title">Packet Loss</span>
          </div>
          <div className="md-chart-body" style={{ height: 240, padding: '14px 8px 8px' }}>
            <ResponsiveContainer>
              <BarChart data={packetLossData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2d3d" />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} interval={5} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} unit="%" width={32} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="packetLoss" fill={COLORS.warning} radius={[3, 3, 0, 0]} name="Packet Loss %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Devices Needing Attention */}
      <div className="md-panel">
        <div className="md-panel-header">
          <span className="md-panel-title">Devices Requiring Attention</span>
          <span className="md-chart-subtitle">{attentionDevices.length} device{attentionDevices.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="md-panel-body">
          {attentionDevices.length > 0 ? attentionDevices.map(d => (
            <div key={d.id} className="md-health-device-row">
              <DeviceTypeIcon type={d.deviceType} size={18} color={d.status === 'OFFLINE' ? COLORS.danger : COLORS.warning} />
              <div className="md-health-device-info">
                <span className="md-health-device-name">{d.hostname || d.name || d.ipAddress}</span>
                <span className="md-health-device-sub">{d.ipAddress} · {d.manufacturer || formatDeviceType(d.deviceType)}</span>
              </div>
              <div className="md-health-device-status">
                <StatusTooltip status={d.status}>
                  <span className={`md-status-dot ${d.status === 'OFFLINE' ? 'danger' : 'warning'}`} />
                </StatusTooltip>
                <span>{d.status}</span>
              </div>
              <div className="md-health-bar-container">
                <div className="md-health-bar">
                  <div className="md-health-bar-fill" style={{
                    width: `${d.healthScore || 0}%`,
                    background: (d.healthScore || 0) >= 70 ? COLORS.warning : COLORS.danger
                  }} />
                </div>
              </div>
              <span className="md-health-score" style={{
                color: (d.healthScore || 0) >= 70 ? COLORS.warning : COLORS.danger
              }}>
                {d.healthScore || 0}%
              </span>
            </div>
          )) : (
            <div className="md-panel-empty">
              <Heart size={24} />
              <span>All devices are healthy!</span>
            </div>
          )}
        </div>
      </div>

      {/* Error Action Modal */}
      {actionModalError && (
        <ErrorActionModal
          error={actionModalError}
          onClose={() => setActionModalError(null)}
          onSubmit={handleUpdateErrorStatus}
          onReportBug={handleReportBug}
        />
      )}

      {showPrompt && (
        <TutorialPrompt
          module={healthTutorial.module}
          moduleName={healthTutorial.moduleName}
          steps={healthTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default MdHealth;
